import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
    AlertTriangle, Plus, Search, Loader2, Shield, CheckCircle,
    Clock, Eye, FileText, XCircle
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { getLocalToday } from '../utils/dateUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const severityConfig = {
    critical: { label: 'Critico', color: 'bg-red-500/10 text-red-500 border-red-500/20' },
    high: { label: 'Alto', color: 'bg-amber-500/10 text-amber-500 border-amber-500/20' },
    medium: { label: 'Medio', color: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20' },
    low: { label: 'Bajo', color: 'bg-blue-500/10 text-blue-500 border-blue-500/20' },
};
const statusConfig = {
    open: { label: 'Abierto', color: 'bg-red-500/10 text-red-400' },
    investigating: { label: 'Investigando', color: 'bg-amber-500/10 text-amber-400' },
    corrective_action: { label: 'Accion Correctiva', color: 'bg-blue-500/10 text-blue-400' },
    closed: { label: 'Cerrado', color: 'bg-emerald-500/10 text-emerald-400' },
};
const categoryLabels = { operational: 'Operacional', maintenance: 'Mantenimiento', cabin: 'Cabina', ground: 'Terrestre', security: 'Seguridad' };
const typeLabels = { incident: 'Incidente', hazard: 'Peligro', observation: 'Observacion', near_miss: 'Cuasi-accidente' };

export default function SMSPage() {
    const { token, hasRole } = useAuth();
    const headers = { Authorization: `Bearer ${token}` };
    const [events, setEvents] = useState([]);
    const [dashboard, setDashboard] = useState(null);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [showCreate, setShowCreate] = useState(false);
    const [showDetail, setShowDetail] = useState(null);
    const [showAction, setShowAction] = useState(null);
    const [newEvent, setNewEvent] = useState({ title: '', description: '', event_type: 'incident', severity: 'low', category: 'operational', location: '', date_occurred: getLocalToday(), flight_number: '' });
    const [newAction, setNewAction] = useState({ description: '', responsible: '', due_date: '' });

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const params = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
            const [eRes, dRes] = await Promise.all([
                axios.get(`${API}/sms/events${params}`, { headers }),
                axios.get(`${API}/sms/dashboard`, { headers }).catch(() => ({ data: { total: 0, open: 0, investigating: 0, closed: 0, critical: 0, high: 0, by_category: {} } }))
            ]);
            setEvents(eRes.data);
            setDashboard(dRes.data);
        } catch { toast.error('Error al cargar datos'); }
        finally { setLoading(false); }
    }, [statusFilter]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const createEvent = async () => {
        if (!newEvent.title) { toast.error('Titulo requerido'); return; }
        try {
            await axios.post(`${API}/sms/events`, newEvent, { headers });
            toast.success('Evento registrado');
            setShowCreate(false);
            setNewEvent({ title: '', description: '', event_type: 'incident', severity: 'low', category: 'operational', location: '', date_occurred: getLocalToday(), flight_number: '' });
            fetchData();
        } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    };

    const openDetail = async (eventId) => {
        try {
            const res = await axios.get(`${API}/sms/events/${eventId}`, { headers });
            setShowDetail(res.data);
        } catch { toast.error('Error al cargar detalle'); }
    };

    const updateStatus = async (eventId, newStatus) => {
        try {
            await axios.put(`${API}/sms/events/${eventId}`, { status: newStatus }, { headers });
            toast.success('Estado actualizado');
            openDetail(eventId);
            fetchData();
        } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    };

    const addAction = async () => {
        if (!newAction.description || !showAction) return;
        try {
            await axios.post(`${API}/sms/events/${showAction}/actions`, newAction, { headers });
            toast.success('Accion correctiva agregada');
            setShowAction(null);
            setNewAction({ description: '', responsible: '', due_date: '' });
            if (showDetail) openDetail(showDetail.id);
            fetchData();
        } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    };

    const closeEvent = async (eventId, notes) => {
        try {
            await axios.patch(`${API}/sms/events/${eventId}/close`, { investigation_notes: notes || '' }, { headers });
            toast.success('Evento cerrado');
            setShowDetail(null);
            fetchData();
        } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    };

    const filtered = events.filter(e => {
        if (!searchTerm) return true;
        const s = searchTerm.toLowerCase();
        return e.title?.toLowerCase().includes(s) || e.description?.toLowerCase().includes(s) || e.flight_number?.toLowerCase().includes(s);
    });

    const canManage = hasRole('admin') || hasRole('ccod') || hasRole('gerencia') || hasRole('calidad');

    return (
        <div className="space-y-6" data-testid="sms-page">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-4xl font-bold tracking-tight uppercase font-['Barlow_Condensed']" data-testid="sms-title">SMS</h1>
                    <p className="text-muted-foreground mt-1">Safety Management System — Gestion de seguridad operacional</p>
                </div>
                <Button onClick={() => setShowCreate(true)} className="btn-primary" data-testid="create-sms-btn"><Plus className="w-4 h-4 mr-2" />Reportar Evento</Button>
            </div>

            {/* Dashboard */}
            {dashboard && (
                <div className="grid grid-cols-2 sm:grid-cols-6 gap-3">
                    <Card className="card-aviation"><CardContent className="p-3 flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-primary" /><div><p className="text-xl font-bold font-mono">{dashboard.total}</p><p className="text-[10px] text-muted-foreground uppercase">Total</p></div></CardContent></Card>
                    <Card className="card-aviation"><CardContent className="p-3 flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-red-500" /><div><p className="text-xl font-bold font-mono">{dashboard.open}</p><p className="text-[10px] text-muted-foreground uppercase">Abiertos</p></div></CardContent></Card>
                    <Card className="card-aviation"><CardContent className="p-3 flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-amber-500" /><div><p className="text-xl font-bold font-mono">{dashboard.investigating}</p><p className="text-[10px] text-muted-foreground uppercase">Investigando</p></div></CardContent></Card>
                    <Card className="card-aviation"><CardContent className="p-3 flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-emerald-500" /><div><p className="text-xl font-bold font-mono">{dashboard.closed}</p><p className="text-[10px] text-muted-foreground uppercase">Cerrados</p></div></CardContent></Card>
                    <Card className="card-aviation border-red-500/20"><CardContent className="p-3 flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-red-500" /><div><p className="text-xl font-bold font-mono text-red-500">{dashboard.critical}</p><p className="text-[10px] text-muted-foreground uppercase">Criticos</p></div></CardContent></Card>
                    <Card className="card-aviation border-amber-500/20"><CardContent className="p-3 flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-amber-500" /><div><p className="text-xl font-bold font-mono text-amber-500">{dashboard.high}</p><p className="text-[10px] text-muted-foreground uppercase">Altos</p></div></CardContent></Card>
                </div>
            )}

            {/* Filters */}
            <div className="flex items-center gap-3">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input placeholder="Buscar eventos..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10 input-aviation" data-testid="sms-search" />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-[160px]" data-testid="sms-status-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        <SelectItem value="open">Abiertos</SelectItem>
                        <SelectItem value="investigating">Investigando</SelectItem>
                        <SelectItem value="corrective_action">Accion Correctiva</SelectItem>
                        <SelectItem value="closed">Cerrados</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {/* Events list */}
            {loading ? (
                <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
            ) : (
                <div className="space-y-2" data-testid="sms-events-list">
                    {filtered.length === 0 ? (
                        <Card className="card-aviation"><CardContent className="p-12 text-center text-muted-foreground"><Shield className="w-12 h-12 mx-auto mb-3 opacity-30" /><p>No hay eventos registrados</p></CardContent></Card>
                    ) : filtered.map(ev => {
                        const sev = severityConfig[ev.severity] || severityConfig.low;
                        const st = statusConfig[ev.status] || statusConfig.open;
                        return (
                            <Card key={ev.id} className={`card-aviation border-l-2 ${sev.color.split(' ')[0].replace('/10', '/30')}`} data-testid={`sms-event-${ev.id}`}>
                                <CardContent className="p-3">
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                                                <Badge className={sev.color}>{sev.label}</Badge>
                                                <Badge className={st.color}>{st.label}</Badge>
                                                <Badge variant="outline" className="text-[10px]">{typeLabels[ev.event_type] || ev.event_type}</Badge>
                                                <Badge variant="outline" className="text-[10px]">{categoryLabels[ev.category] || ev.category}</Badge>
                                                {ev.flight_number && <span className="text-[10px] font-mono text-muted-foreground">{ev.flight_number}</span>}
                                            </div>
                                            <p className="text-sm font-medium">{ev.title}</p>
                                            <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">{ev.description}</p>
                                        </div>
                                        <div className="text-right text-xs text-muted-foreground shrink-0">
                                            <p>{ev.date_occurred}</p>
                                            <p className="mt-0.5">{ev.reported_by_name}</p>
                                            <Button variant="ghost" size="sm" className="h-6 mt-1 text-xs" onClick={() => openDetail(ev.id)} data-testid={`view-event-${ev.id}`}>
                                                <Eye className="w-3 h-3 mr-1" />Ver
                                            </Button>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>
            )}

            {/* Create Event Dialog */}
            <Dialog open={showCreate} onOpenChange={setShowCreate}>
                <DialogContent className="sm:max-w-[550px] max-h-[85vh] overflow-y-auto">
                    <DialogHeader><DialogTitle className="font-['Barlow_Condensed'] text-xl uppercase flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-primary" />Reportar Evento de Seguridad</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div className="space-y-1.5"><Label>Titulo</Label><Input value={newEvent.title} onChange={e => setNewEvent({...newEvent, title: e.target.value})} placeholder="Breve descripcion del evento" data-testid="sms-title-input" /></div>
                        <div className="space-y-1.5"><Label>Descripcion</Label><Textarea value={newEvent.description} onChange={e => setNewEvent({...newEvent, description: e.target.value})} rows={3} placeholder="Descripcion detallada..." data-testid="sms-desc-input" /></div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5"><Label>Tipo</Label>
                                <Select value={newEvent.event_type} onValueChange={v => setNewEvent({...newEvent, event_type: v})}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent><SelectItem value="incident">Incidente</SelectItem><SelectItem value="hazard">Peligro</SelectItem><SelectItem value="observation">Observacion</SelectItem><SelectItem value="near_miss">Cuasi-accidente</SelectItem></SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-1.5"><Label>Severidad</Label>
                                <Select value={newEvent.severity} onValueChange={v => setNewEvent({...newEvent, severity: v})}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent><SelectItem value="low">Bajo</SelectItem><SelectItem value="medium">Medio</SelectItem><SelectItem value="high">Alto</SelectItem><SelectItem value="critical">Critico</SelectItem></SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5"><Label>Categoria</Label>
                                <Select value={newEvent.category} onValueChange={v => setNewEvent({...newEvent, category: v})}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent><SelectItem value="operational">Operacional</SelectItem><SelectItem value="maintenance">Mantenimiento</SelectItem><SelectItem value="cabin">Cabina</SelectItem><SelectItem value="ground">Terrestre</SelectItem><SelectItem value="security">Seguridad</SelectItem></SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-1.5"><Label>Fecha</Label><Input type="date" value={newEvent.date_occurred} onChange={e => setNewEvent({...newEvent, date_occurred: e.target.value})} data-testid="sms-date" /></div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5"><Label>Vuelo (opcional)</Label><Input value={newEvent.flight_number} onChange={e => setNewEvent({...newEvent, flight_number: e.target.value.toUpperCase()})} placeholder="AD-401" /></div>
                            <div className="space-y-1.5"><Label>Ubicacion</Label><Input value={newEvent.location} onChange={e => setNewEvent({...newEvent, location: e.target.value})} placeholder="ICAO / Area" /></div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowCreate(false)}>Cancelar</Button>
                        <Button onClick={createEvent} className="btn-primary" data-testid="submit-sms-event">Reportar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Detail Dialog */}
            <Dialog open={!!showDetail} onOpenChange={() => setShowDetail(null)}>
                <DialogContent className="sm:max-w-[600px] max-h-[85vh] overflow-y-auto">
                    {showDetail && (
                        <>
                            <DialogHeader><DialogTitle className="font-['Barlow_Condensed'] text-xl uppercase flex items-center gap-2"><FileText className="w-5 h-5 text-primary" />Evento #{showDetail.id.slice(0,8)}</DialogTitle></DialogHeader>
                            <div className="space-y-4">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <Badge className={severityConfig[showDetail.severity]?.color}>{severityConfig[showDetail.severity]?.label}</Badge>
                                    <Badge className={statusConfig[showDetail.status]?.color}>{statusConfig[showDetail.status]?.label}</Badge>
                                    <Badge variant="outline">{typeLabels[showDetail.event_type]}</Badge>
                                    <Badge variant="outline">{categoryLabels[showDetail.category]}</Badge>
                                </div>
                                <div>
                                    <h3 className="font-bold text-lg">{showDetail.title}</h3>
                                    <p className="text-sm text-muted-foreground mt-1">{showDetail.description}</p>
                                </div>
                                <div className="grid grid-cols-2 gap-2 text-sm">
                                    <div><span className="text-muted-foreground">Fecha:</span> {showDetail.date_occurred}</div>
                                    <div><span className="text-muted-foreground">Reportado por:</span> {showDetail.reported_by_name}</div>
                                    {showDetail.flight_number && <div><span className="text-muted-foreground">Vuelo:</span> {showDetail.flight_number}</div>}
                                    {showDetail.location && <div><span className="text-muted-foreground">Ubicacion:</span> {showDetail.location}</div>}
                                </div>

                                {/* Corrective Actions */}
                                <div className="p-3 rounded-lg bg-muted/30 border border-border/30">
                                    <div className="flex items-center justify-between mb-2">
                                        <h4 className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Acciones Correctivas ({showDetail.corrective_actions?.length || 0})</h4>
                                        {canManage && showDetail.status !== 'closed' && (
                                            <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={() => setShowAction(showDetail.id)} data-testid="add-action-btn"><Plus className="w-3 h-3 mr-1" />Agregar</Button>
                                        )}
                                    </div>
                                    {showDetail.corrective_actions?.length > 0 ? showDetail.corrective_actions.map(a => (
                                        <div key={a.id} className="flex items-start gap-2 text-sm mb-2 pb-2 border-b border-border/20 last:border-0">
                                            <CheckCircle className={`w-4 h-4 mt-0.5 ${a.status === 'completed' ? 'text-emerald-500' : 'text-muted-foreground'}`} />
                                            <div>
                                                <p>{a.description}</p>
                                                <p className="text-xs text-muted-foreground">{a.responsible} {a.due_date ? `— Vence: ${a.due_date}` : ''}</p>
                                            </div>
                                        </div>
                                    )) : <p className="text-xs text-muted-foreground">Sin acciones correctivas</p>}
                                </div>

                                {/* Status actions */}
                                {canManage && showDetail.status !== 'closed' && (
                                    <div className="flex items-center gap-2 flex-wrap">
                                        {showDetail.status === 'open' && <Button size="sm" variant="outline" onClick={() => updateStatus(showDetail.id, 'investigating')} data-testid="set-investigating">Iniciar Investigacion</Button>}
                                        {showDetail.status === 'investigating' && <Button size="sm" variant="outline" onClick={() => updateStatus(showDetail.id, 'corrective_action')}>Accion Correctiva</Button>}
                                        <Button size="sm" className="btn-primary" onClick={() => closeEvent(showDetail.id, '')} data-testid="close-event">
                                            <XCircle className="w-3.5 h-3.5 mr-1" />Cerrar Evento
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>

            {/* Add Action Dialog */}
            <Dialog open={!!showAction} onOpenChange={() => setShowAction(null)}>
                <DialogContent className="sm:max-w-[400px]">
                    <DialogHeader><DialogTitle className="font-['Barlow_Condensed'] text-xl uppercase">Accion Correctiva</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div className="space-y-1.5"><Label>Descripcion</Label><Textarea value={newAction.description} onChange={e => setNewAction({...newAction, description: e.target.value})} rows={3} data-testid="action-desc" /></div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5"><Label>Responsable</Label><Input value={newAction.responsible} onChange={e => setNewAction({...newAction, responsible: e.target.value})} data-testid="action-resp" /></div>
                            <div className="space-y-1.5"><Label>Fecha limite</Label><Input type="date" value={newAction.due_date} onChange={e => setNewAction({...newAction, due_date: e.target.value})} /></div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowAction(null)}>Cancelar</Button>
                        <Button onClick={addAction} className="btn-primary" data-testid="submit-action">Agregar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
